import classNames from 'classnames/bind';
import styles from './Shingle.module.scss';
import TableClassicReport from '../base/TableClassicReport';
import { useEffect, useState } from 'react';
import { IPaginationReport, IShingle, IShingleColor } from '~/utils/interface/report';
import reportApi from '~/apis/report';
import { ColumnType } from '~/utils/types/common';

const cx = classNames.bind(styles);

const STATUS_SHINGLE = [
  {
    value: "LEAD",
    label: "Lead",
  },
  {
    value: "APPOINTMENT_SCHEDULED",
    label: "Appointment Scheduled",
  },
  {
    value: "FILLING_CLAIM",
    label: "Filling Claim",
  },
  {
    value: "WAITING_ON_SCOPE",
    label: "Waiting on Scope",
  },
  {
    value: "SUPPLEMENTING",
    label: "Supplementing",
  },
  {
    value: "ESTIMATING",
    label: "Estimating",
  },
  {
    value: "ESTIMATE_SENT",
    label: "Estimate Sent",
  },
  {
    value: "COMMERCIAL",
    label: "Commercial",
  },
  {
    value: "HOLD_SIGNING",
    label: "Hold Signing",
  },
  {
    value: "SIGNED_CONTRACT",
    label: "Signed Contract",
  },
  {
    value: "PRODUCTION_REVIEW",
    label: "Production Review",
  },
  {
    value: "LOST",
    label: "Lost",
  },
  {
    value: "MATERIAL_ORDERED",
    label: "Material Ordered",
  },
  {
    value: "SECONDARY_TRADES",
    label: "Secondary Trades",
  },
  {
    value: "JOB_SCHEDULED",
    label: "Job Scheduled",
  },
  {
    value: "JOB_IN_PROGRESS",
    label: "Job In Progress",
  },
  {
    value: "FINAL_WALKTHROUGH",
    label: "Final Walkthrough",
  },
  {
    value: "JOB_COMPLETED",
    label: "Job Completed",
  },
  {
    value: "INVOICED_INSURANCE",
    label: "Invoiced Insurance",
  },
  {
    value: "INVOICED_COMMERCIAL",
    label: "Invoiced Commercial",
  },
  {
    value: "PAID_N_CLOSED",
    label: "Paid & Closed",
  },
  {
    value: "PAID_N_HAPPY",
    label: "Paid & Happy",
  },
  {
    value: "PAID_N_LEFT_REVIEW",
    label: "Paid & Left Review",
  },
  {
    value: "DELINQUENT_DO_NOT_ADD_JOBS",
    label: "Delinquent (Do Not Add Jobs)",
  },
];
const Shingle = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IShingle[]>([]);
  const [pagination, setPagination] = useState<IPaginationReport>({
    totalItems: 0,
    totalPage: 0,
    page: 1,
    limit: 10,
    skip: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await reportApi.getShingle({ page: pagination.page });
        setData(response.data?.data?.responses ?? []);
        setPagination(response.data?.data?.pagination ?? {
          totalItems: 0,
          totalPage: 0,
          page: 1,
          limit: 10,
          skip: 0,
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch shingle', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [pagination.page]);

  const columns: Array<ColumnType> = [
    {
      id: "name",
      labelHeader: "Name",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>
            {shingle.relatedContact?.name || "-"} - {shingle.job?.uniqueCode || "-"}
          </p>
        </div>
      ),
    },
    {
      id: "trade",
      labelHeader: "Trade",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.trade || "-"}</p>
        </div>
      ),
    },
    {
      id: "saleRep",
      labelHeader: "Sale Rep",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.saleRep.name || "-"}</p>
        </div>
      ),
    },
    {
      id: "primaryContact",
      labelHeader: "Primary Contact",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.relatedContact.name || "-"}</p>
        </div>
      ),
    },
    {
      id: "assignedTo",
      labelHeader: "Assigned To",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.assignedTo.name || "-"}</p>
        </div>
      ),
    },
    {
      id: "shingleManufacturer",
      labelHeader: "Shingle Mfr",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.shingleManufacturer || "-"}</p>
        </div>
      ),
    },
    {
      id: "shingleColor",
      labelHeader: "Shingle Color",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.shingleColor || "-"}</p>
        </div>
      ),
    },
    {
      id: "addressLine",
      labelHeader: "Address Line",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.addressLine || "-"}</p>
        </div>
      ),
    },
    {
      id: "zip",
      labelHeader: "Zip",
      renderCell: (shingleColor: IShingleColor) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingleColor.zip || "-"}</p>
        </div>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{STATUS_SHINGLE.find(item => item.value === shingle.status)?.label || "-"}</p>
        </div>
      ),
    },
    {
      id: "type",
      labelHeader: "Type",
      renderCell: (shingle: IShingle) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{shingle.type || "-"}</p>
        </div>
      ),
    },
  ];

  return (
    <TableClassicReport
      title="Shingle"
      columns={columns}
      data={data}
      page={pagination.page}
      totalPages={pagination.totalPage}
      totalItems={pagination.totalItems}
      itemsPerPage={pagination.limit}
      onPageChange={(page) => {
        setPagination({ ...pagination, page });
      }}
      isLoading={loading}
    />
  );
};

export default Shingle;
